import React, { useState, useEffect } from 'react';
import { client, urlFor } from '../../client'; // Import Sanity client and urlFor
import { PortableText } from '@portabletext/react'; // Import PortableText

// Consistent Loading Spinner
const LoadingSpinner = () => (
  <div className="flex justify-center items-center min-h-screen">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#2a4865]"></div>
    <p className="ml-4 text-lg text-gray-700">Loading...</p>
  </div>
);

// Consistent Error Message
const ErrorMessage = ({ message }) => (
  <div className="container mx-auto px-4 py-12 text-center">
    <p className="text-red-600 font-semibold">{message || 'Error loading page content. Please try again later.'}</p>
  </div>
);

// Simple Portable Text components (default handling is sufficient for footer)
const ptComponents = {};

const CellphonePolicy = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPageData = async () => {
      setLoading(true);
      try {
        const query = `*[_type == "cellphonePolicyPage" && _id == "cellphonePolicyPageContent"][0] {
          bannerTitle,
          bannerImage {
            asset->{
              _id,
              url
            }
          },
          mainContentTitle,
          introText,
          policyItems,
          footerText
        }`;
        const data = await client.fetch(query);
        if (data) {
          setPageData(data);
        } else {
          setError('Cellphone Policy Page content not found.');
        }
        setLoading(false);
      } catch (err) {
        console.error("Failed to fetch Cellphone Policy page data:", err);
        setError(err.message || 'Failed to load page content. Please try again later.');
        setLoading(false);
      }
    };

    fetchPageData();
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (!pageData) return <ErrorMessage message="No content available for this page." />;

  return (
    <div className="bg-white">
      {/* Hero Banner with Image Overlay - Dynamic */}
      <section className="relative h-[40vh] max-h-[400px]">
        <div className="w-full h-full relative overflow-hidden">
          {pageData.bannerImage?.asset?.url ? (
            <img 
              src={urlFor(pageData.bannerImage).url()} 
              alt={pageData.bannerTitle || 'PS52 Cell Phone Policy'} 
              className="absolute inset-0 w-full h-full object-cover object-center" 
              loading="lazy"
            />
          ) : (
             <div className="absolute inset-0 w-full h-full bg-gray-300 flex items-center justify-center">
              <span className="text-gray-500">Banner Image Missing</span>
            </div> 
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20"></div>
        </div>
        
        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-6 md:px-12 lg:px-16">
            <div className="max-w-3xl pb-12 md:pb-16 lg:pb-16 text-left">
              <div className="w-16 h-1 bg-yellow-500 mb-6"></div>
              
              <h1 className="text-white text-4xl md:text-5xl font-bold mb-2 leading-tight text-left">
                {pageData.bannerTitle || 'Cell Phone Policy'} {/* Dynamic */}
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="bg-white p-8 rounded-lg shadow-md max-w-4xl mx-auto">
            {pageData.mainContentTitle && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-[#2a4865] mb-6 relative after:content-[''] after:absolute after:w-16 after:h-1 after:bg-yellow-500 after:-bottom-3 after:left-0">
                  {pageData.mainContentTitle}
                </h2>
                
                {pageData.introText && (
                  <p className="mt-8 text-gray-700 font-medium text-lg">
                    {pageData.introText}
                  </p>
                )}
              </div>
            )}
            
            {pageData.policyItems?.length > 0 && (
              <div className="bg-[#2a4865]/5 p-6 rounded-lg">
                <ul className="space-y-5">
                  {pageData.policyItems.map((item, index) => (
                    <li key={index} className="flex items-start">
                       {/* Keep exact number circle styling */} 
                      <div className="bg-[#691e31] text-white rounded-full h-6 w-6 flex items-center justify-center font-bold mr-3 mt-0.5 flex-shrink-0">
                        {index + 1}
                      </div>
                      <span className="text-lg text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          
          {/* Additional Visual Element - Dynamic Footer */}
          {pageData.footerText && (
            <div className="max-w-5xl mx-auto mt-8 bg-[#691e31] text-white p-6 rounded-lg text-center">
              <div className="flex items-center justify-center">
                 {/* Keep exact SVG icon */}
                <svg xmlns="http://www.w3.org/2000/svg" className="h-13 w-13 mr-3 text-yellow-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"> {/* Added flex-shrink-0 */} 
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                {/* Render Portable Text for footer */} 
                <div className="text-xl font-bold text-white text-left"> {/* Added text-left for better alignment */} 
                  <PortableText value={pageData.footerText} components={ptComponents} />
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default CellphonePolicy; 